// A tabela do ajuste mês a mês de um alvo da EAP (design D20, spec target-month-adjustments):
// uma linha por competência, contínua (os meses vazios no meio também aparecem, para o
// valor poder ir para um mês sem nada), com a referência, o ajustado editável e a diferença.
//
// Só apresentação: o estado é o de `useMonthEdit`, recebido em `edit`. O saldo a
// redistribuir precisa fechar em zero — quem trava o salvar é o editor, com o motivo.
import { Fragment, useState } from "react";

import { competenceText } from "./format";
import {
  centsToReaisInput,
  monthsBetween,
  nextMonthWithin,
  parseReaisToCents,
  reais,
} from "./monthEdit";

const MoveForm = ({ month, available, months, onMove, onCancel }) => {
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState(() => centsToReaisInput(available));
  const cents = parseReaisToCents(amount);
  const valid =
    to !== "" && Number.isSafeInteger(cents) && cents > 0 && cents <= available;
  return (
    <div
      className="ampli-month-edit__move"
      role="group"
      aria-label={`Mover valor de ${competenceText(month)}`}
    >
      <label className="ampli-rule-editor__field">
        <span className="card-label">Mover para</span>
        <select
          className="default-input"
          aria-label={`Mês de destino de ${competenceText(month)}`}
          value={to}
          onChange={(event) => setTo(event.target.value)}
        >
          <option value="">Escolha o mês</option>
          {months
            .filter((candidate) => candidate !== month)
            .map((candidate) => (
              <option key={candidate} value={candidate}>
                {competenceText(candidate)}
              </option>
            ))}
        </select>
      </label>
      <label className="ampli-rule-editor__field">
        <span className="card-label">Valor (R$)</span>
        <input
          type="text"
          inputMode="decimal"
          className="default-input"
          aria-label={`Valor a mover de ${competenceText(month)}`}
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
      </label>
      <div className="ampli-month-edit__move-actions">
        <button
          type="button"
          className="button button-ghost small"
          onClick={onCancel}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="button button-accent small"
          disabled={!valid}
          onClick={() => onMove(to, cents)}
        >
          Mover
        </button>
      </div>
      {!valid &&
      amount !== "" &&
      Number.isSafeInteger(cents) &&
      cents > available ? (
        <p className="card-label" role="status">{`O mês tem ${reais(
          available,
        )}.`}</p>
      ) : null}
    </div>
  );
};

const MonthEditTable = ({
  edit,
  referenceLabel = "Referência",
  readOnly = false,
  currentMonth = null,
}) => {
  // O texto em digitação de cada mês: o campo não pode reformatar "12," enquanto o cliente
  // ainda digita. Sai do rascunho ao perder o foco.
  const [drafts, setDrafts] = useState({});
  const [moving, setMoving] = useState(null);

  const { months, reference, edited, window } = edit;
  // Destinos do "Mover para…": todos os meses da janela, não só os da tabela, para o valor
  // poder ir além do último mês com valor.
  const destinations =
    window?.last && months.length > 0
      ? monthsBetween(months[0], window.last)
      : months;
  const totals = months.reduce(
    (sum, month) => ({
      reference: sum.reference + (reference.get(month) ?? 0),
      edited: sum.edited + (edited.get(month) ?? 0),
    }),
    { reference: 0, edited: 0 },
  );

  return (
    <div className="ampli-month-edit">
      <div
        className="ampli-rule-projection ampli-month-edit__table"
        role="region"
        aria-label="Ajuste mês a mês"
        tabIndex={0}
      >
        <table>
          <caption className="visually-hidden">
            Ajuste mês a mês do alvo
          </caption>
          <thead>
            <tr>
              <th scope="col">Mês</th>
              <th scope="col" className="num">
                {referenceLabel}
              </th>
              <th scope="col" className="num">
                Ajustado
              </th>
              <th scope="col" className="num">
                Diferença
              </th>
              {!readOnly ? (
                <th scope="col">
                  <span className="visually-hidden">Ações</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {months.map((month) => {
              const base = reference.get(month) ?? 0;
              const value = edited.get(month) ?? 0;
              const delta = value - base;
              const past = currentMonth !== null && month < currentMonth;
              const draft = drafts[month];
              const next = nextMonthWithin(month, window);
              return (
                <Fragment key={month}>
                  <tr
                    className={
                      [
                        delta !== 0 ? "is-changed" : null,
                        past ? "is-past" : null,
                      ]
                        .filter(Boolean)
                        .join(" ") || undefined
                    }
                    data-testid={`month-edit-row-${month}`}
                  >
                    <th scope="row">
                      {competenceText(month)}
                      {past ? (
                        <span className="ampli-month-edit__past">
                          {" "}
                          (passado)
                        </span>
                      ) : null}
                    </th>
                    <td className="num">{reais(base)}</td>
                    <td className="num">
                      {readOnly ? (
                        reais(value)
                      ) : (
                        <input
                          type="text"
                          inputMode="decimal"
                          className="default-input ampli-month-edit__input"
                          aria-label={`Ajustado em ${competenceText(month)}`}
                          value={draft ?? centsToReaisInput(value)}
                          aria-invalid={
                            draft !== undefined &&
                            Number.isNaN(parseReaisToCents(draft))
                          }
                          onChange={(event) => {
                            const text = event.target.value;
                            setDrafts((current) => ({
                              ...current,
                              [month]: text,
                            }));
                            const cents = parseReaisToCents(text);
                            if (Number.isSafeInteger(cents) && cents >= 0)
                              edit.setMonth(month, cents);
                          }}
                          onBlur={() =>
                            setDrafts(({ [month]: _left, ...rest }) => rest)
                          }
                        />
                      )}
                    </td>
                    <td
                      className={`num${
                        delta > 0 ? " is-up" : delta < 0 ? " is-down" : ""
                      }`}
                    >
                      {delta === 0
                        ? "—"
                        : `${delta > 0 ? "+" : "−"}${reais(Math.abs(delta))}`}
                    </td>
                    {!readOnly ? (
                      <td className="ampli-month-edit__actions">
                        <button
                          type="button"
                          className="button button-ghost small"
                          disabled={value <= 0 || next === null}
                          aria-label={`Adiar ${competenceText(
                            month,
                          )} para o mês seguinte`}
                          onClick={() => {
                            setDrafts({});
                            edit.postpone(month);
                          }}
                        >
                          Adiar para o mês seguinte
                        </button>
                        <button
                          type="button"
                          className="button button-ghost small"
                          disabled={value <= 0}
                          aria-expanded={moving === month}
                          aria-label={`Mover valor de ${competenceText(
                            month,
                          )} para outro mês`}
                          onClick={() =>
                            setMoving((current) =>
                              current === month ? null : month,
                            )
                          }
                        >
                          Mover para…
                        </button>
                      </td>
                    ) : null}
                  </tr>
                  {moving === month && !readOnly ? (
                    <tr className="ampli-month-edit__move-row">
                      <td colSpan={5}>
                        <MoveForm
                          month={month}
                          available={value}
                          months={destinations}
                          onCancel={() => setMoving(null)}
                          onMove={(to, cents) => {
                            setDrafts({});
                            edit.move(month, to, cents);
                            setMoving(null);
                          }}
                        />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">Total</th>
              <td className="num">{reais(totals.reference)}</td>
              <td className="num">{reais(totals.edited)}</td>
              <td className="num">—</td>
              {!readOnly ? <td /> : null}
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="ampli-month-edit__footer">
        <p
          className={`ampli-month-edit__balance${
            edit.balance !== 0 ? " is-open" : ""
          }`}
          role="status"
          data-testid="month-edit-balance"
        >
          {edit.balance === 0
            ? "Saldo a redistribuir: R$ 0,00"
            : edit.balance > 0
            ? `Saldo a redistribuir: faltam ${reais(edit.balance)} em algum mês`
            : `Saldo a redistribuir: sobram ${reais(
                -edit.balance,
              )}; tire de algum mês`}
        </p>
        {!readOnly ? (
          <div className="ampli-month-edit__tools">
            <button
              type="button"
              className="button button-ghost small"
              disabled={!edit.canAddMonth}
              onClick={edit.addMonth}
            >
              Adicionar mês
            </button>
            <button
              type="button"
              className="button button-ghost small"
              disabled={!edit.dirty}
              onClick={() => {
                setDrafts({});
                setMoving(null);
                edit.undo();
              }}
            >
              Desfazer alterações
            </button>
            {edit.data?.entries?.length > 0 ? (
              <button
                type="button"
                className="button button-ghost small"
                onClick={() => {
                  setDrafts({});
                  setMoving(null);
                  edit.backToReference();
                }}
              >
                Voltar à referência
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default MonthEditTable;
